import type { GraphNode } from '../../../types'

/**
 * Ownership graph colours and node styling, shared by the live graph and the
 * SVG export. Mirrors the »Kortbordet« tokens in tailwind.config.js and
 * DESIGN.md → Ejerskabsgrafen. Only the look lives here; layout and
 * behaviour stay in the engine.
 */
export const GRAPH_COLORS = {
  ink:         '#12151A',
  inkMuted:    '#545D6A',
  inkFaint:    '#6E7683',
  surface:     '#FFFFFF',
  canvas:      '#F4F5F7',
  graphCanvas: '#FBFBFC',
  nodeStroke:  '#C3C9D1',
  edge:        '#AEB5BF',
  flag:        '#C93A22',
  flagWash:    '#FCEEEA',
  focus:       '#2B50D6',
} as const

export const GRAPH_FONT = "'Hanken Grotesk', ui-sans-serif, system-ui, sans-serif"

export const NODE_LABEL_FONT_SIZE = 12.5
export const COMPANY_NODE_RX = 6

/** Flag under the name of a foreign node (3:2). */
export const NODE_FLAG_W = 18
export const NODE_FLAG_H = 12

export interface NodeStyleState {
  isBeneficialOwner: boolean
  isHovered?:        boolean
  isInChain?:        boolean
}

export interface NodeStyle {
  fill:        string
  stroke:      string
  strokeWidth: number
  text:        string
  fontWeight:  number
}

export function nodeHasForeignCountry(node: GraphNode): boolean {
  return !!node.countryCode && node.countryCode.toUpperCase() !== 'DK'
}

/**
 * Same shape, same thing: white company rectangle, white person pill, ink
 * pill for the beneficial owner, flag-red wash for foreign companies, and
 * an ink border on the company itself.
 */
export function graphNodeStyle(node: GraphNode, state: NodeStyleState): NodeStyle {
  const C = GRAPH_COLORS
  const isRoot = !!node.isRoot
  const isForeign = node.kind === 'foreign-company'
  const emphasised = !!(state.isHovered || state.isInChain)

  if (node.truncated) {
    return {
      fill: C.canvas,
      stroke: C.nodeStroke,
      strokeWidth: emphasised ? 1.5 : 1,
      text: C.inkFaint,
      fontWeight: 400,
    }
  }
  if (state.isBeneficialOwner) {
    return {
      fill: C.ink,
      stroke: C.ink,
      strokeWidth: 1,
      text: C.surface,
      fontWeight: 600,
    }
  }
  if (isRoot) {
    return { fill: C.surface, stroke: C.ink, strokeWidth: 2.5, text: C.ink, fontWeight: 700 }
  }
  if (isForeign) {
    return {
      fill: C.flagWash,
      stroke: C.flag,
      strokeWidth: emphasised ? 1.75 : 1,
      text: C.ink,
      fontWeight: 400,
    }
  }
  return {
    fill: C.surface,
    stroke: emphasised ? C.ink : C.nodeStroke,
    strokeWidth: emphasised ? 1.5 : 1,
    text: C.ink,
    fontWeight: 400,
  }
}
